import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, User, Mail, Shield, CreditCard, Calendar,
  Lightbulb, Edit2, Check, X, Lock, Save, Smartphone, Copy, AlertTriangle
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_COLORS = [
  '#FF3B30', '#FF9500', '#FFD60A', '#34C759', '#007AFF',
  '#5856D6', '#AF52DE', '#FF2D55', '#A2845E', '#0A0A0A'
];

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', bio: '', avatar_url: '' });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });

  // 2FA state
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaStep, setTwoFaStep] = useState(null); // null | 'qr' | 'verify' | 'done'
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [totpCode, setTotpCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable2fa, setShowDisable2fa] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/profile`, { withCredentials: true });
      setProfile(data);
      setForm({ name: data.name || '', bio: data.bio || '', avatar_url: data.avatar_url || '' });
      setTwoFaEnabled(data.is_2fa_enabled || false);
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, form, { withCredentials: true });
      toast.success('Profile updated!');
      setEditing(false);
      fetchProfile();
      if (checkAuth) checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Fill in all password fields'); return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters'); return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match'); return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      toast.success('Password changed!');
      setChangingPassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  // 2FA handlers
  const startTwoFaSetup = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/mfa/setup`, {}, { withCredentials: true });
      setQrCodeUrl(data.qr_code_url);
      setManualKey(data.manual_key);
      setBackupCodes(data.backup_codes);
      setTwoFaStep('qr');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setSaving(false);
    }
  };

  const verifyTwoFa = async () => {
    if (totpCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/mfa/verify-setup`, { totp_code: totpCode }, { withCredentials: true });
      setBackupCodes(data.backup_codes);
      setTwoFaStep('done');
      setTwoFaEnabled(true);
      toast.success('2FA enabled!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setSaving(false);
      setTotpCode('');
    }
  };

  const disableTwoFa = async () => {
    if (!disablePassword) { toast.error('Enter your password'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/mfa/disable`, { password: disablePassword }, { withCredentials: true });
      setTwoFaEnabled(false);
      setShowDisable2fa(false);
      setDisablePassword('');
      toast.success('2FA disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setSaving(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied!');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[var(--obsidian)] border-t-transparent animate-spin"></div>
      </div>
    );
  }

  const tierLabels = { free: 'Free', pro: 'Pro', business: 'Business', founding: 'Founding Member' };

  return (
    <div className="min-h-screen bg-[var(--main-bg)] p-4 sm:p-8" data-testid="profile-page">
      <Toaster position="top-right" />
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6 transition-colors"
          data-testid="profile-back-btn"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Profile
        </h1>

        {/* Avatar + Name Section */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Avatar */}
            <div
              className="w-24 h-24 flex items-center justify-center text-white text-3xl font-black shrink-0 border-2 border-[var(--border-default)]"
              style={{
                backgroundColor: profile?.avatar_url || getAvatarColor(profile?.name),
                fontFamily: "'Space Grotesk', sans-serif"
              }}
              data-testid="profile-avatar"
            >
              {getInitials(profile?.name)}
            </div>

            <div className="flex-1 text-center sm:text-left">
              {editing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1">Name</label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm font-medium"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1">Bio</label>
                    <textarea
                      value={form.bio}
                      onChange={(e) => setForm({ ...form, bio: e.target.value })}
                      placeholder="Tell us about yourself..."
                      rows={3}
                      className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm resize-none"
                      data-testid="profile-bio-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1">Avatar Color</label>
                    <div className="flex gap-2 flex-wrap">
                      {AVATAR_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setForm({ ...form, avatar_url: color })}
                          className={`w-8 h-8 border-2 transition-transform ${
                            form.avatar_url === color ? 'border-[var(--obsidian)] scale-110' : 'border-[var(--border-subtle)]'
                          }`}
                          style={{ backgroundColor: color }}
                          data-testid={`avatar-color-${color.replace('#', '')}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="selected-state font-bold px-6 py-2 border-2 border-[var(--border-default)] disabled:opacity-50 flex items-center gap-2 text-sm"
                      data-testid="profile-save-btn"
                    >
                      {saving ? 'Saving...' : <><Save size={14} /> Save</>}
                    </button>
                    <button
                      onClick={() => { setEditing(false); setForm({ name: profile?.name || '', bio: profile?.bio || '', avatar_url: profile?.avatar_url || '' }); }}
                      className="px-6 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors"
                      data-testid="profile-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-black mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {profile?.name || 'Unnamed'}
                  </h2>
                  {profile?.bio && (
                    <p className="text-sm text-[var(--text-secondary)] mb-2">{profile.bio}</p>
                  )}
                  <div className="flex items-center gap-2 justify-center sm:justify-start text-sm text-[var(--text-secondary)]">
                    <Mail size={14} />
                    <span>{profile?.email}</span>
                  </div>
                  <button
                    onClick={() => setEditing(true)}
                    className="mt-3 px-4 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors flex items-center gap-2"
                    data-testid="profile-edit-btn"
                  >
                    <Edit2 size={14} /> Edit Profile
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#007AFF]"></div>
            <div className="flex items-center gap-2 mb-2">
              <Shield size={16} className="text-[#007AFF]" />
              <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)]">Role</span>
            </div>
            <div className="text-lg font-black capitalize" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {profile?.role || 'user'}
            </div>
          </div>

          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#FFD60A]"></div>
            <div className="flex items-center gap-2 mb-2">
              <CreditCard size={16} className="text-[#FFD60A]" />
              <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)]">Plan</span>
            </div>
            <div className="text-lg font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {tierLabels[profile?.subscription_tier] || 'Free'}
            </div>
          </div>

          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#34C759]"></div>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb size={16} className="text-[#34C759]" />
              <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)]">Ideas</span>
            </div>
            <div className="text-lg font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {profile?.idea_count || 0}
            </div>
          </div>
        </div>

        {/* Member Since */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-5 mb-6">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-[var(--text-secondary)]" />
            <span className="text-sm text-[var(--text-secondary)]">Member since</span>
            <span className="text-sm font-bold">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
            </span>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock size={18} />
              <h3 className="font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Security</h3>
            </div>
            {!changingPassword && (
              <button
                onClick={() => setChangingPassword(true)}
                className="px-4 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors"
                data-testid="change-password-btn"
              >
                Change Password
              </button>
            )}
          </div>
          {changingPassword && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1">Current Password</label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1">New Password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handlePasswordChange}
                  disabled={saving}
                  className="selected-state font-bold px-6 py-2 border-2 border-[var(--border-default)] disabled:opacity-50 text-sm"
                  data-testid="save-password-btn"
                >
                  {saving ? 'Saving...' : 'Update Password'}
                </button>
                <button
                  onClick={() => { setChangingPassword(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}
                  className="px-6 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors"
                  data-testid="cancel-password-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {!changingPassword && (
            <p className="text-sm text-[var(--text-secondary)]">
              Password must be at least 8 characters.
            </p>
          )}
        </div>

        {/* Two-Factor Authentication */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6" data-testid="2fa-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Smartphone size={18} />
              <h3 className="font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Two-Factor Authentication</h3>
            </div>
            {twoFaEnabled && !twoFaStep && !showDisable2fa && (
              <span className="px-3 py-1 bg-[#34C759]/15 border border-[#34C759] text-[#34C759] text-xs font-mono uppercase tracking-wider font-bold">
                Enabled
              </span>
            )}
          </div>

          {/* Status: Not enabled, no setup in progress */}
          {!twoFaEnabled && !twoFaStep && (
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Add an extra layer of security. When enabled, you'll need a code from your authenticator app (Google Authenticator, Authy, etc.) every time you sign in.
              </p>
              <button
                onClick={startTwoFaSetup}
                disabled={saving}
                className="selected-state font-bold px-6 py-2 border-2 border-[var(--border-default)] disabled:opacity-50 text-sm flex items-center gap-2"
                data-testid="enable-2fa-btn"
              >
                <Shield size={14} /> Enable 2FA
              </button>
            </div>
          )}

          {/* Step 1: QR Code */}
          {twoFaStep === 'qr' && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-secondary)]">
                Scan this QR code with your authenticator app:
              </p>
              <div className="flex justify-center p-4 bg-white border-2 border-[var(--border-default)]">
                {qrCodeUrl && <img src={qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />}
              </div>
              <div className="bg-[var(--surface-alt)] border border-[var(--border-subtle)] p-3">
                <p className="text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1">Can't scan? Enter manually:</p>
                <p className="font-mono text-sm break-all select-all" data-testid="2fa-manual-key">{manualKey}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setTwoFaStep('verify')}
                  className="selected-state font-bold px-6 py-2 border-2 border-[var(--border-default)] text-sm"
                  data-testid="2fa-next-btn"
                >
                  Next: Verify Code
                </button>
                <button
                  onClick={() => { setTwoFaStep(null); setQrCodeUrl(''); setManualKey(''); }}
                  className="px-6 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Verify */}
          {twoFaStep === 'verify' && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-secondary)]">
                Enter the 6-digit code from your authenticator app to confirm setup:
              </p>
              <input
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-center text-2xl font-mono tracking-[0.5em]"
                data-testid="2fa-verify-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={verifyTwoFa}
                  disabled={saving || totpCode.length < 6}
                  className="selected-state font-bold px-6 py-2 border-2 border-[var(--border-default)] disabled:opacity-50 text-sm flex items-center gap-2"
                  data-testid="2fa-verify-btn"
                >
                  {saving ? 'Verifying...' : <><Check size={14} /> Verify & Enable</>}
                </button>
                <button
                  onClick={() => setTwoFaStep('qr')}
                  className="px-6 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success + Backup Codes */}
          {twoFaStep === 'done' && (
            <div className="space-y-4">
              <div className="bg-[#34C759]/10 border-2 border-[#34C759] p-4">
                <p className="text-sm font-bold text-[#34C759]">2FA is now active!</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-[#FF9500]" />
                  <p className="text-sm font-bold">Save your backup codes</p>
                </div>
                <p className="text-xs text-[var(--text-secondary)] mb-3">
                  If you lose access to your authenticator app, you can use these codes to sign in. Each code can only be used once. Store them somewhere safe.
                </p>
                <div className="bg-[var(--surface-alt)] border-2 border-[var(--border-default)] p-4 font-mono text-sm grid grid-cols-2 gap-2" data-testid="backup-codes-list">
                  {backupCodes.map((code, i) => (
                    <span key={i} className="py-1">{code}</span>
                  ))}
                </div>
                <button
                  onClick={copyBackupCodes}
                  className="mt-3 px-4 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors flex items-center gap-2"
                  data-testid="copy-backup-codes-btn"
                >
                  <Copy size={14} /> Copy Codes
                </button>
              </div>
              <button
                onClick={() => setTwoFaStep(null)}
                className="selected-state font-bold px-6 py-2 border-2 border-[var(--border-default)] text-sm"
                data-testid="2fa-done-btn"
              >
                Done
              </button>
            </div>
          )}

          {/* Already enabled: show disable option */}
          {twoFaEnabled && !twoFaStep && !showDisable2fa && (
            <div className="mt-4">
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                Your account is protected with two-factor authentication.
              </p>
              <button
                onClick={() => setShowDisable2fa(true)}
                className="px-4 py-2 border-2 border-[#FF3B30] text-[#FF3B30] text-sm font-bold hover:bg-[#FF3B30]/10 transition-colors"
                data-testid="disable-2fa-btn"
              >
                Disable 2FA
              </button>
            </div>
          )}

          {/* Disable 2FA confirmation */}
          {showDisable2fa && (
            <div className="space-y-3 mt-4">
              <p className="text-sm text-[var(--text-secondary)]">
                Enter your password to disable 2FA:
              </p>
              <input
                type="password"
                value={disablePassword}
                onChange={e => setDisablePassword(e.target.value)}
                placeholder="Current password"
                className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                data-testid="disable-2fa-password"
              />
              <div className="flex gap-2">
                <button
                  onClick={disableTwoFa}
                  disabled={saving}
                  className="bg-[#FF3B30] text-white font-bold px-6 py-2 border-2 border-[var(--border-default)] disabled:opacity-50 text-sm"
                  data-testid="confirm-disable-2fa-btn"
                >
                  {saving ? 'Disabling...' : 'Confirm Disable'}
                </button>
                <button
                  onClick={() => { setShowDisable2fa(false); setDisablePassword(''); }}
                  className="px-6 py-2 border-2 border-[var(--border-default)] text-sm font-bold hover:bg-[var(--surface-alt)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
